import { Role } from "@/types/auth";

export const FEATURE_IDS = [
  "newOrder",
  "openOrders",
  "orderTracking",
  "recurringOrders",
  "clients",
  "media",
  "catalog",
  "deliveries",
  "deliveryManagement",
  "orderListing",
  "verifications",
  "cashRegisters",
  "statistics",
  "subscriptions",
  "workshopValidations",
  "flowerPurchases",
  "myTasks",
  "events",
  "calendar",
  "leave",
  "team",
  "settings",
] as const;

export type FeatureId = (typeof FEATURE_IDS)[number];

export type FeatureAccessException = "manager" | "verificationDelegate";

interface FeaturePolicy {
  enabled: boolean;
  roles: Record<Role, boolean>;
  exceptions?: readonly FeatureAccessException[];
}

export const FEATURE_ACCESS: Record<FeatureId, FeaturePolicy> = {
  newOrder: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  openOrders: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  orderTracking: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: true,
      [Role.LIVREUR]: false,
    },
  },
  recurringOrders: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: false,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  clients: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  media: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  catalog: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  deliveries: {
    enabled: true,
    roles: {
      [Role.ADMIN]: false,
      [Role.SENIOR]: false,
      [Role.COMPTABLE]: false,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: true,
    },
  },
  deliveryManagement: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: false,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  orderListing: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  verifications: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: false,
      [Role.COMPTABLE]: false,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
    exceptions: ["verificationDelegate"],
  },
  cashRegisters: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  statistics: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  subscriptions: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  workshopValidations: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: false,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  flowerPurchases: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: false,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  myTasks: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: true,
      [Role.ATELIER]: true,
      [Role.LIVREUR]: true,
    },
  },
  events: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: true,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
  },
  calendar: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: true,
      [Role.ATELIER]: true,
      [Role.LIVREUR]: true,
    },
  },
  leave: {
    enabled: false,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: true,
      [Role.ATELIER]: true,
      [Role.LIVREUR]: true,
    },
  },
  team: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: false,
      [Role.BOUTIQUE]: false,
      [Role.FLEURISTE]: false,
      [Role.PREPARATEUR]: false,
      [Role.ATELIER]: false,
      [Role.LIVREUR]: false,
    },
    exceptions: ["manager"],
  },
  settings: {
    enabled: true,
    roles: {
      [Role.ADMIN]: true,
      [Role.SENIOR]: true,
      [Role.COMPTABLE]: true,
      [Role.BOUTIQUE]: true,
      [Role.FLEURISTE]: true,
      [Role.PREPARATEUR]: true,
      [Role.ATELIER]: true,
      [Role.LIVREUR]: true,
    },
  },
};

export interface FeatureAccessContext {
  role: Role | null;
  isManager: boolean;
  isVerificationDelegate: boolean;
}

export function canAccessFeature(
  feature: FeatureId,
  context: FeatureAccessContext,
): boolean {
  const policy = FEATURE_ACCESS[feature];

  if (!policy.enabled) return false;
  if (context.role && policy.roles[context.role]) return true;

  return (
    (policy.exceptions?.includes("manager") && context.isManager) ||
    (policy.exceptions?.includes("verificationDelegate") &&
      context.isVerificationDelegate) ||
    false
  );
}
